import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import constate from "constate";
import Shepherd from "shepherd.js";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";

import "./styles.css";
import { TOUR_TYPES } from "./constants";
import { preventMouseEvents, allowMouseEvents } from "./utils";

/*
  Tour state to be used by constate.

  This state will add a general tour that can be used to create
  different tour types when needed. For example, the product tour is a
  predefined tour type, that consist of different parts and steps and can be
  shown by just providing the tour type to the provided methods of this state.
*/
const TourState = () => {
  // General
  const history = useHistory();

  // Selectors
  const userData = useSelector(state => state.auth.userData);

  // Refs
  const tour = useRef(null);

  // State
  const [currentStepId, setCurrentStepId] = useState("");

  // Callbacks
  /*
    Create a Shepherd tour with default options.

    TODO: Add a way to adjust the default options by providing a second
     argument to this function.
   */
  const createTour = useCallback(tourName => {
    tour.current = new Shepherd.Tour({
      useModalOverlay: true,
      exitOnEsc: false,
      keyboardNavigation: false,
      tourName,
      defaultStepOptions: {
        scrollTo: { behavior: "smooth", block: "center" },
        modalOverlayOpeningPadding: 10,
        modalOverlayOpeningRadius: 4,
        popperOptions: {
          modifiers: [{ name: "offset", options: { offset: [0, 23] } }]
        },
        when: {
          show: () => {
            allowMouseEvents();
          },
          hide: () => {
            preventMouseEvents();
          }
        }
      }
    });
  }, []);

  // Effects
  useEffect(() => {
    /*
      Add the pulsing border to the dom, to be used by steps when a part of the
      page should be noticed by the user.This pulsing border element is a fixed
      positioned element, that moved by each step around the button or area that
      user should notice and most of the time click on.
     */
    if (!document.querySelector("#tour_pulsing_border")) {
      const pulsingBorderElement = document.createElement("div");
      pulsingBorderElement.setAttribute("id", "tour_pulsing_border");
      document.querySelector("body").appendChild(pulsingBorderElement);
    }
  }, []);

  // Memos
  /*
    These methods are the methods that provided by this state so other part of
    the WA can use it to interact with the product tour.
   */
  const methods = useMemo(
    () => ({
      /*
        Starting a new tour based on the provided tour type.
      */
      startTour(tourType, options = {}, getSteps) {
        if (
          !Object.values(TOUR_TYPES).includes(tourType) ||
          TOUR_TYPES[tourType] === undefined
        ) {
          throw new Error(
            `Trying to create missing tour type. tourType: ${tourType}`
          );
        }

        createTour(tourType);

        // Add tour steps
        tour.current.addSteps(getSteps(options, userData, history));

        /*
          Add event listeners to the tour instance.
         */
        if (options.onCancel) {
          tour.current.on("cancel", () => {
            tour.current = null;
            setCurrentStepId("");
            options.onCancel();
          });
        }

        if (options.onComplete) {
          tour.current.on("complete", () => {
            tour.current = null;
            setCurrentStepId("");
            options.onComplete();
          });
        }

        if (options.onShow) {
          tour.current.on("show", ({ step }) => {
            if (step) {
              setCurrentStepId(step.id);
              options.onShow(step);
            }
          });
        }

        setTimeout(() => {
          tour.current.start();
        }, 30);
      },
      cancelTour() {
        if (tour.current) {
          allowMouseEvents();
          tour.current.cancel();
        }
      },
      /*
        Check if an active tour is active right now.
       */
      hasActiveTourCurrently() {
        return tour.current !== null;
      },
      /*
        Check if the current tour step is equal to a stepID.
       */
      isTourStepId(stepId) {
        if (tour.current && currentStepId) {
          return currentStepId === stepId;
        }
        return false;
      },
      /*
        Update the options of the already added steps to the tour, suing the
        step id.
       */
      updateStep(stepId, newOptions) {
        if (tour.current) {
          const step = tour.current.getById(stepId);
          step.updateStepOptions({ ...step.options, ...newOptions });
        }
      },
      /*
        GO to next step in the current tour.
       */
      goToNext() {
        if (tour.current) {
          tour.current.next();
        }
      },
      /*
        Show the pulsing border. By default, the pulsing border is not shown
        and should be shown to the user eiter by the step when.show option or
        by the component based on some logic, like valid form inputs. In case of
        the second way, this method can be used.
       */
      showPulsingBorder() {
        const pulsingBorderElement = document.querySelector(
          "#tour_pulsing_border"
        );
        if (pulsingBorderElement) {
          pulsingBorderElement.classList.add("tour-show-pulsing-border");
        }
      },
      /*
        Hide the already shown pulsing border.
       */
      hidePulsingBorder() {
        const pulsingBorderElement = document.querySelector(
          "#tour_pulsing_border"
        );
        if (pulsingBorderElement) {
          pulsingBorderElement.classList.remove("tour-show-pulsing-border");
        }
      },
      /*
        Show the primary tour button pulsing border
       */
      showPrimaryButtonPulsingBorder() {
        const primaryButton = document.querySelector(
          ".shepherd-element:not([hidden]) .tour-button-primary"
        );
        if (primaryButton) {
          primaryButton.classList.add("tour-show-pulsing-border");
        }
      }
    }),
    [createTour, currentStepId, history, userData]
  );

  return { methods };
};

const [TourProvider, useTourMethods] = constate(
  TourState,
  state => state.methods
);

export default TourProvider;
export { useTourMethods };
